// Cache Service for localStorage management
export interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

export interface CacheConfig {
  ttl: number; // Time to live in milliseconds
  maxSize: number; // Maximum number of items in cache
}

class CacheService {
  private readonly CACHE_PREFIX = 'sb_cache_';
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly DEFAULT_MAX_SIZE = 100;

  private cacheEnabled = false; // Global flag to enable/disable caching (disabled by default)

  setCacheEnabled(enabled: boolean) {
    this.cacheEnabled = enabled;
  }

  isCacheEnabled() {
    return this.cacheEnabled;
  }

  // Cache configuration for different endpoints
  private cacheConfigs: Record<string, CacheConfig> = {
    users: { ttl: 2 * 60 * 1000, maxSize: 50 }, // 2 minutes
    doctors: { ttl: 1 * 60 * 1000, maxSize: 50 },
    forms: { ttl: 15 * 60 * 1000, maxSize: 30 }, // 15 minutes
    formResponses: { ttl: 5 * 60 * 1000, maxSize: 100 }, // 5 minutes
    investigations: { ttl: 10 * 60 * 1000, maxSize: 30 },
    prescriptions: { ttl: 10 * 60 * 1000, maxSize: 50 },
  };

  // Generate cache key
  private getCacheKey(endpoint: string, id?: string): string {
    return `${this.CACHE_PREFIX}${endpoint}${id ? `_${id}` : ''}`;
  }

  // Check if cache is available and not expired
  private isCacheValid<T>(cacheKey: string): boolean {
    try {
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return false;

      const cacheItem: CacheItem<T> = JSON.parse(cached);
      return Date.now() < cacheItem.expiresAt;
    } catch {
      return false;
    }
  }

  // Get cached data
  get<T>(endpoint: string, id?: string): T | null {
    if (!this.cacheEnabled) return null;
    const cacheKey = this.getCacheKey(endpoint, id);
    
    if (!this.isCacheValid<T>(cacheKey)) {
      this.remove(endpoint, id);
      return null;
    }

    try {
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;

      const cacheItem: CacheItem<T> = JSON.parse(cached);
      return cacheItem.data;
    } catch {
      return null;
    }
  }

  // Set cache data
  set<T>(endpoint: string, data: T, id?: string): void {
    if (!this.cacheEnabled) return;
    const cacheKey = this.getCacheKey(endpoint, id);
    const config = this.cacheConfigs[endpoint] || { ttl: this.DEFAULT_TTL, maxSize: this.DEFAULT_MAX_SIZE };
    
    const cacheItem: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + config.ttl,
    };

    try {
      // Check cache size and clean if necessary
      this.cleanupCache(endpoint, config.maxSize);
      
      localStorage.setItem(cacheKey, JSON.stringify(cacheItem));
    } catch (error) {
      console.warn('Failed to set cache:', error);
    }
  }

  // Remove specific cache item
  remove(endpoint: string, id?: string): void {
    if (!this.cacheEnabled) return;
    const cacheKey = this.getCacheKey(endpoint, id);
    try {
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.warn('Failed to remove cache:', error);
    }
  }

  // Clear all cache for an endpoint
  clearEndpoint(endpoint: string): void {
    if (!this.cacheEnabled) return;
    try {
      const keys = Object.keys(localStorage);
      const prefix = this.getCacheKey(endpoint);
      
      keys.forEach(key => {
        if (key.startsWith(prefix)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Failed to clear endpoint cache:', error);
    }
  }

  // Clear all cache
  clearAll(): void {
    if (!this.cacheEnabled) return;
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.CACHE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Failed to clear all cache:', error);
    }
  }

  // Cleanup cache for an endpoint (remove oldest items if over max size)
  private cleanupCache(endpoint: string, maxSize: number): void {
    try {
      const keys = Object.keys(localStorage);
      const prefix = this.getCacheKey(endpoint);
      const endpointKeys = keys.filter(key => key.startsWith(prefix));

      if (endpointKeys.length >= maxSize) {
        // Sort by timestamp (oldest first)
        const keyTimestamps = endpointKeys.map(key => {
          try {
            const cached = localStorage.getItem(key);
            if (cached) {
              const cacheItem = JSON.parse(cached);
              return { key, timestamp: cacheItem.timestamp };
            }
          } catch {
            return { key, timestamp: 0 };
          }
          return { key, timestamp: 0 };
        }).sort((a, b) => a.timestamp - b.timestamp);

        // Remove oldest items
        const itemsToRemove = endpointKeys.length - maxSize + 1;
        keyTimestamps.slice(0, itemsToRemove).forEach(({ key }) => {
          localStorage.removeItem(key);
        });
      }
    } catch (error) {
      console.warn('Failed to cleanup cache:', error);
    }
  }

  // Invalidate cache when data is updated
  invalidateOnUpdate(endpoint: string, id?: string): void {
    if (!this.cacheEnabled) return;
    // Remove the specific item cache
    this.remove(endpoint, id);
    
    // Also remove the list cache to ensure fresh data
    this.remove(endpoint);
  }

  // Invalidate cache when data is deleted
  invalidateOnDelete(endpoint: string, id?: string): void {
    if (!this.cacheEnabled) return;
    // Remove the specific item cache
    this.remove(endpoint, id);
    
    // Also remove the list cache to ensure fresh data
    this.remove(endpoint);
  }

  // Get cache statistics (for debugging)
  getCacheStats(): Record<string, number> {
    if (!this.cacheEnabled) return {};
    try {
      const keys = Object.keys(localStorage);
      const stats: Record<string, number> = {};
      
      Object.keys(this.cacheConfigs).forEach(endpoint => {
        const prefix = this.getCacheKey(endpoint);
        const count = keys.filter(key => key.startsWith(prefix)).length;
        stats[endpoint] = count;
      });
      
      return stats;
    } catch {
      return {};
    }
  }
}

export const cacheService = new CacheService(); 